// ============================================================
// src/pages/athlete/SessionInput.js
// Rollaco Cycling Academy — Tahap 4 (Strava mode aktif)
// ============================================================

import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import {
  stravaLogin,
  stravaGetActivities,
  getValidStravaToken,
  isStravaConnected,
  stravaDisconnect,
  mapStravaToSessionLog,
} from '../../lib/strava'
import {
  parseGpx,
  parseFit,
  detectFileType,
  readFileAsText,
  readFileAsArrayBuffer,
} from '../../lib/fitParser'
import { dismissSessionReminder } from '../../lib/notifications'

// ── Jadwal sesi per hari ─────────────────────────────────────
const SESSION_TYPES = {
  0: { label: 'Long Ride',      icon: '🗺️', color: '#60a5fa' },
  1: { label: 'MTB / XC',      icon: '🚵', color: '#34d399' },
  2: { label: 'Road Passing',   icon: '⚡', color: '#facc15' },
  3: { label: 'Road Passing',   icon: '⚡', color: '#facc15' },
  4: { label: 'MTB / XC',      icon: '🚵', color: '#34d399' },
  5: { label: 'Indoor Trainer', icon: '🎯', color: '#a78bfa' },
  6: { label: 'Hill Repeat',   icon: '🏔️', color: '#f87171' },
}

const ROAD_MODES     = ['Interval', 'Tim Rantai', 'ITT']
const LONG_RIDE_TYPES = ['Flat', 'Rolling + Tanjakan']

// ── RPE descriptions ─────────────────────────────────────────
const RPE_INFO = {
  1:  { label: 'Sangat Ringan', desc: 'Sangat santai, hampir tidak terasa usaha. Bisa napas normal lewat hidung, cocok untuk recovery.' },
  2:  { label: 'Ringan',        desc: 'Kecepatan santai/komuter. Napas teratur, masih bisa ngobrol panjang lebar dengan mudah.' },
  3:  { label: 'Ringan',        desc: 'Kecepatan santai/komuter. Napas teratur, masih bisa ngobrol panjang lebar dengan mudah.' },
  4:  { label: 'Sedang',        desc: 'Kecepatan touring/aerobik dasar. Napas lebih dalam, masih bisa bicara kalimat panjang tapi mulai butuh jeda.' },
  5:  { label: 'Sedang',        desc: 'Kecepatan touring/aerobik dasar. Napas lebih dalam, masih bisa bicara kalimat panjang tapi mulai butuh jeda.' },
  6:  { label: 'Berat',         desc: 'Kecepatan tempo. Napas berat, hanya bisa bicara 3–4 kata, mulai terasa panas di otot paha.' },
  7:  { label: 'Berat',         desc: 'Kecepatan tempo/bersepeda cepat. Usaha serius tapi masih bisa ditahan. Napas berat, kalimat pendek.' },
  8:  { label: 'Sangat Berat',  desc: 'Kecepatan time trial. Napas tersengal-sengal, sulit bicara (hanya 1–2 kata), otot sangat lelah.' },
  9:  { label: 'Usaha Maksimal',desc: 'Sprint pendek atau tanjakan curam. Tidak bisa bicara sama sekali, napas megap-megap, tidak bisa bertahan lama.' },
  10: { label: 'Maksimal',      desc: 'Sprint all-out. Panik, otot kaku/sakit, hanya bisa bertahan beberapa detik hingga 1 menit.' },
}

const getRPEColor = (n) => {
  if (n <= 3) return '#34d399'
  if (n <= 5) return '#a3e635'
  if (n <= 7) return '#facc15'
  if (n === 8) return '#fb923c'
  return '#ef4444'
}

// ── Styles ───────────────────────────────────────────────────
const inputStyle = {
  width: '100%',
  padding: '0.65rem 0.75rem',
  background: '#0f172a',
  border: '1px solid #334155',
  borderRadius: '8px',
  color: '#f1f5f9',
  fontSize: '1rem',
  boxSizing: 'border-box',
}

const labelStyle = {
  color: '#94a3b8',
  fontSize: '0.75rem',
  marginBottom: '0.25rem',
  display: 'block',
}

const today = SESSION_TYPES[new Date().getDay()]

// ── Format durasi dari detik ──────────────────────────────────
const formatDuration = (seconds) => {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  return h > 0 ? `${h}j ${m}m` : `${m} menit`
}

// ── Format tanggal aktivitas Strava ──────────────────────────
const formatStravaDate = (isoStr) => {
  const d = new Date(isoStr)
  return d.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })
}

// ── Komponen utama ────────────────────────────────────────────
export default function SessionInput({ profile, athleteId, onSuccess, onCancel }) {
  const [mode, setMode] = useState('manual') // manual | strava | file

  // ── State File upload ─────────────────────────────────────
  const [fileData, setFileData]       = useState(null)
  const [fileName, setFileName]       = useState('')
  const [fileLoading, setFileLoading] = useState(false)
  const [fileError, setFileError]     = useState(null)
  const [fileStep, setFileStep]       = useState('idle') // idle | preview | rpe

  // ── State Strava ──────────────────────────────────────────
  const [stravaConnected, setStravaConnected]     = useState(false)
  const [stravaActivities, setStravaActivities]   = useState([])
  const [stravaLoading, setStravaLoading]         = useState(false)
  const [stravaError, setStravaError]             = useState(null)
  const [selectedActivity, setSelectedActivity]   = useState(null)
  const [stravaStep, setStravaStep]               = useState('idle')

  // Cek status koneksi Strava saat load
  useEffect(() => {
  const checkStrava = async () => {
  const connected = await isStravaConnected(profile.id)
  setStravaConnected(connected)
}
checkStrava()
  }, [profile.id])

  // ── State form manual ─────────────────────────────────────
  const [loading, setLoading]     = useState(false)
  const [error, setError]         = useState(null)
  const [success, setSuccess]     = useState(false)

  const [rpe, setRpe]                     = useState('')
  const [cadence, setCadence]             = useState('')
  const [avgSpeed, setAvgSpeed]           = useState('')
  const [distance, setDistance]           = useState('')
  const [duration, setDuration]           = useState('')
  const [heartRate, setHeartRate]         = useState('')
  const [powerWatts, setPowerWatts]       = useState('')
  const [notes, setNotes]                 = useState('')
  const [roadMode, setRoadMode]           = useState('Interval')
  const [longRideType, setLongRideType]   = useState('Flat')

  // Hill repeat
  const [hillReps, setHillReps] = useState([
    { session: 1, reps: [{ duration: '', speed: '' }] },
    { session: 2, reps: [{ duration: '', speed: '' }] },
  ])

  const dayOfWeek   = new Date().getDay()
  const isRoadPassing = dayOfWeek === 2 || dayOfWeek === 3
  const isHillRepeat  = dayOfWeek === 6
  const isLongRide    = dayOfWeek === 0

  // ── Hill repeat helpers ───────────────────────────────────
  const addRep = (si) => {
    const u = [...hillReps]
    u[si].reps.push({ duration: '', speed: '' })
    setHillReps(u)
  }
  const removeRep = (si, ri) => {
    const u = [...hillReps]
    u[si].reps = u[si].reps.filter((_, i) => i !== ri)
    setHillReps(u)
  }
  const updateRep = (si, ri, field, value) => {
    const u = [...hillReps]
    u[si].reps[ri][field] = value
    setHillReps(u)
  }

  // ── File: parse file yang diupload ───────────────────────
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    setFileError(null)
    setFileLoading(true)
    setFileData(null)
    setFileName(file.name)

    const type = detectFileType(file.name)
    if (!type) {
      setFileError('Format tidak didukung. Gunakan file .fit atau .gpx')
      setFileLoading(false)
      return
    }

    try {
      let result
      if (type === 'gpx') {
        const text = await readFileAsText(file)
        result = parseGpx(text)
      } else {
        const buffer = await readFileAsArrayBuffer(file)
        result = await parseFit(buffer)
      }
      setFileData(result)
      setFileStep('preview')
    } catch (err) {
      setFileError(err.message)
    }
    setFileLoading(false)
  }

  // ── File: simpan ke Supabase ──────────────────────────────
  const handleFileSubmit = async () => {
    if (!rpe) { setError('RPE wajib diisi'); return }
    setLoading(true)
    setError(null)

    try {
      const todayStr = new Date().toISOString().split('T')[0]
      const dayType  = ['long_ride','mtb_xc','road_passing','road_passing','mtb_xc','indoor','hill_repeat'][new Date().getDay()]

      let sessionId
      const { data: existing } = await supabase
        .from('sessions')
        .select('id')
        .eq('session_date', todayStr)
        .eq('day_type', dayType)
        .single()

      if (existing) {
        sessionId = existing.id
      } else {
        const { data: newSess, error: se } = await supabase
          .from('sessions')
          .insert({ session_date: todayStr, day_type: dayType, created_by: profile.id })
          .select('id')
          .single()
        if (se) throw se
        sessionId = newSess.id
      }

      const { error: logError } = await supabase.from('session_logs').insert({
        athlete_id:  athleteId,
        session_id:  sessionId,
        rpe:         parseInt(rpe),
        cadence:     fileData.cadence,
        avg_speed:   fileData.avg_speed,
        distance:    fileData.distance,
        duration:    fileData.duration,
        heart_rate:  fileData.heart_rate,
        power_watts: fileData.power_watts,
        notes:       notes || fileData.notes || null,
        source: 'file',
      })
      if (logError) throw logError

      // ── Dismiss session_reminder setelah submit sukses ──
      await dismissSessionReminder(profile.id)

      setSuccess(true)
      setTimeout(() => onSuccess && onSuccess(), 1500)
    } catch (err) {
      setError(err.message)
    }
    setLoading(false)
  }

  // ── Strava: ambil aktivitas ───────────────────────────────
  const handleStravaFetch = async () => {
    setStravaLoading(true)
    setStravaError(null)
    try {
      const token = await getValidStravaToken(profile.id)
      if (!token) {
        setStravaConnected(false)
        setStravaError('Token tidak valid. Silakan hubungkan ulang Strava.')
        setStravaLoading(false)
        return
      }
      const activities = await stravaGetActivities(token, 15)
      setStravaActivities(activities)
      setStravaStep('list')
    } catch (err) {
      setStravaError(err.message || 'Gagal ambil aktivitas Strava.')
    }
    setStravaLoading(false)
  }

  // ── Strava: pilih aktivitas & auto-fill form ──────────────
  const handleSelectActivity = (activity) => {
    const mapped = mapStravaToSessionLog(activity)
    setSelectedActivity({ ...activity, mapped })
    setStravaStep('rpe')
  }

  // ── Strava: konfirmasi & simpan ───────────────────────────
  const handleStravaSubmit = async () => {
    if (!rpe) { setError('RPE wajib diisi'); return }
    setLoading(true)
    setError(null)

    try {
      const todayStr = new Date().toISOString().split('T')[0]
      const dayType  = ['long_ride','mtb_xc','road_passing','road_passing','mtb_xc','indoor','hill_repeat'][new Date().getDay()]

      let sessionId
      const { data: existing } = await supabase
        .from('sessions')
        .select('id')
        .eq('session_date', todayStr)
        .eq('day_type', dayType)
        .single()

      if (existing) {
        sessionId = existing.id
      } else {
        const { data: newSess, error: se } = await supabase
          .from('sessions')
          .insert({ session_date: todayStr, day_type: dayType, created_by: profile.id })
          .select('id')
          .single()
        if (se) throw se
        sessionId = newSess.id
      }

      const mapped = selectedActivity.mapped
      const { error: logError } = await supabase.from('session_logs').insert({
        athlete_id:         athleteId,
        session_id:         sessionId,
        rpe:                parseInt(rpe),
        cadence:            mapped.cadence,
        avg_speed:          mapped.avg_speed,
        distance:           mapped.distance,
        duration:           mapped.duration,
        heart_rate:         mapped.heart_rate,
        power_watts:        mapped.power_watts,
        notes:              mapped.notes,
        strava_activity_id: mapped.strava_activity_id,
        source:             'strava',
      })
      if (logError) throw logError

      // ── Dismiss session_reminder setelah submit sukses ──
      await dismissSessionReminder(profile.id)

      setSuccess(true)
      setTimeout(() => onSuccess && onSuccess(), 1500)
    } catch (err) {
      setError(err.message)
    }
    setLoading(false)
  }

  // ── Manual: simpan ────────────────────────────────────────
  const handleManualSubmit = async () => {
    if (!rpe) { setError('RPE wajib diisi'); return }
    setLoading(true)
    setError(null)

    try {
      const todayStr = new Date().toISOString().split('T')[0]
      const dayType  = ['long_ride','mtb_xc','road_passing','road_passing','mtb_xc','indoor','hill_repeat'][new Date().getDay()]

      let sessionId
      const { data: existing } = await supabase
        .from('sessions')
        .select('id')
        .eq('session_date', todayStr)
        .eq('day_type', dayType)
        .single()

      if (existing) {
        sessionId = existing.id
      } else {
        const { data: newSess, error: se } = await supabase
          .from('sessions')
          .insert({
            session_date: todayStr,
            day_type:     dayType,
            mode:         isRoadPassing ? roadMode : null,
            created_by:   profile.id,
          })
          .select('id')
          .single()
        if (se) throw se
        sessionId = newSess.id
      }

      const { data: log, error: logError } = await supabase
        .from('session_logs')
        .insert({
          athlete_id:  athleteId,
          session_id:  sessionId,
          rpe:         parseInt(rpe),
          cadence:     cadence     ? parseInt(cadence)     : null,
          avg_speed:   avgSpeed    ? parseFloat(avgSpeed)  : null,
          distance:    distance    ? parseFloat(distance)  : null,
          duration:    duration    ? parseInt(duration)    : null,
          heart_rate:  heartRate   ? parseInt(heartRate)   : null,
          power_watts: powerWatts  ? parseInt(powerWatts)  : null,
          notes:       notes || null,
          source:      'manual',
        })
        .select('id')
        .single()
      if (logError) throw logError

      // Hill repeat detail
      if (isHillRepeat) {
        const hillData = []
        hillReps.forEach((s, si) => {
          s.reps.forEach((rep, ri) => {
            if (rep.duration || rep.speed) {
              hillData.push({
                session_log_id: log.id,
                session_number: si + 1,
                rep_number:     ri + 1,
                duration_seconds: rep.duration ? parseInt(rep.duration) : null,
                avg_speed:        rep.speed    ? parseFloat(rep.speed)  : null,
              })
            }
          })
        })
        if (hillData.length > 0) {
          const { error: he } = await supabase.from('hill_repeat_logs').insert(hillData)
          if (he) throw he
        }
      }

      // ── Dismiss session_reminder setelah submit sukses ──
      await dismissSessionReminder(profile.id)

      setSuccess(true)
      setTimeout(() => onSuccess && onSuccess(), 1500)
    } catch (err) {
      setError(err.message)
    }
    setLoading(false)
  }

  // ── Success screen ────────────────────────────────────────
  if (success) return (
    <div style={{
      minHeight: '100vh', background: '#0f172a',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexDirection: 'column', gap: '1rem',
    }}>
      <div style={{ fontSize: '4rem' }}>✅</div>
      <div style={{ color: '#34d399', fontSize: '1.2rem', fontWeight: 'bold' }}>
        Sesi berhasil disimpan!
      </div>
    </div>
  )

  // ── RPE selector (shared oleh manual & strava) ────────────
  const RPESelector = () => {
    const activeRpe = parseInt(rpe)
    return (
      <div style={{ marginBottom: '1rem' }}>
        <label style={labelStyle}>
          RPE (1–10) <span style={{ color: '#ef4444' }}>*</span>
        </label>

        {/* Tombol RPE */}
        <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
          {[1,2,3,4,5,6,7,8,9,10].map(n => {
            const selected = rpe === n.toString()
            const col = getRPEColor(n)
            return (
              <button
                key={n}
                onClick={() => setRpe(n.toString())}
                style={{
                  flex: '1 1 calc(10% - 0.35rem)',
                  minWidth: '2.2rem',
                  padding: '0.5rem 0',
                  background: selected ? col : '#1e293b',
                  border: selected ? `2px solid ${col}` : '2px solid #334155',
                  borderRadius: '6px',
                  color: selected ? '#0f172a' : '#94a3b8',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 'bold',
                  transition: 'all 0.15s',
                }}
              >
                {n}
              </button>
            )
          })}
        </div>

        {/* Deskripsi RPE — muncul saat dipilih */}
        {rpe && RPE_INFO[activeRpe] && (
          <div style={{
            marginTop: '0.5rem',
            padding: '0.5rem 0.75rem',
            background: `${getRPEColor(activeRpe)}15`,
            border: `1px solid ${getRPEColor(activeRpe)}44`,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.5rem',
          }}>
            <span style={{
              fontSize: '0.7rem',
              fontWeight: 'bold',
              color: getRPEColor(activeRpe),
              whiteSpace: 'nowrap',
              marginTop: '1px',
            }}>
              RPE {activeRpe} — {RPE_INFO[activeRpe].label}
            </span>
            <span style={{
              fontSize: '0.68rem',
              color: '#94a3b8',
              lineHeight: 1.5,
            }}>
              {RPE_INFO[activeRpe].desc}
            </span>
          </div>
        )}
      </div>
    )
  }

  // ── Render utama ──────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', color: '#f1f5f9' }}>

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center',
        padding: '1rem 1.5rem',
        borderBottom: '1px solid #1e293b',
        background: '#0a0f1e',
        gap: '1rem',
      }}>
        <button onClick={onCancel} style={{
          background: 'none', border: '1px solid #334155',
          color: '#94a3b8', padding: '0.4rem 0.75rem',
          borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem',
        }}>
          ← Batal
        </button>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '1.2rem' }}>{today.icon}</span>
            <span style={{ fontWeight: 'bold', color: today.color }}>{today.label}</span>
          </div>
          <div style={{ color: '#94a3b8', fontSize: '0.7rem' }}>Input Sesi Hari Ini</div>
        </div>
      </div>

      <div style={{ padding: '1rem 1.5rem' }}>

        {/* Mode selector */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem', marginBottom: '1.25rem' }}>
          {[
            { id: 'manual', label: '📝 Manual', desc: 'Isi form' },
            { id: 'strava', label: '🔄 Strava', desc: stravaConnected ? 'Terhubung ✓' : 'Sync API' },
            { id: 'file',   label: '📁 File',   desc: '.fit/.gpx' },
          ].map(m => (
            <button key={m.id} onClick={() => {
              setMode(m.id)
              setStravaStep('idle')
              setFileStep('idle')
              setFileData(null)
              setFileName('')
              setFileError(null)
              setStravaError(null)
              setError(null)
            }} style={{
              padding: '0.75rem 0.5rem',
              background: mode === m.id ? `${today.color}22` : '#1e293b',
              border: mode === m.id ? `2px solid ${today.color}` : '2px solid #334155',
              borderRadius: '10px',
              color: mode === m.id ? today.color : '#94a3b8',
              cursor: 'pointer', textAlign: 'center',
            }}>
              <div style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{m.label}</div>
              <div style={{ fontSize: '0.65rem', opacity: 0.8 }}>{m.desc}</div>
            </button>
          ))}
        </div>

        {/* ══════════════════════════════════════════════════
            MODE: STRAVA
        ══════════════════════════════════════════════════ */}
        {mode === 'strava' && (
          <>
            {/* Belum connect → tampilkan tombol connect */}
            {!stravaConnected && (
              <div style={{
                background: '#fb923c15',
                border: '2px dashed #fb923c55',
                borderRadius: '12px',
                padding: '2rem',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🚴</div>
                <div style={{ color: '#fb923c', fontWeight: 'bold', fontSize: '1rem', marginBottom: '0.25rem' }}>
                  Hubungkan ke Strava
                </div>
                <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '1.25rem', lineHeight: 1.5 }}>
                  Izinkan Rollaco membaca aktivitas Strava kamu agar data sesi otomatis terisi.
                </div>
                <button
                  onClick={() => stravaLogin()}
                  style={{
                    padding: '0.75rem 2rem',
                    background: '#fb923c',
                    border: 'none',
                    borderRadius: '10px',
                    color: '#0f172a',
                    fontWeight: 'bold',
                    fontSize: '0.95rem',
                    cursor: 'pointer',
                  }}
                >
                  🔗 Connect Strava
                </button>
                {stravaError && (
                  <div style={{ color: '#f87171', fontSize: '0.75rem', marginTop: '0.75rem' }}>
                    {stravaError}
                  </div>
                )}
              </div>
            )}

            {/* Sudah connect & belum ambil aktivitas */}
            {stravaConnected && stravaStep === 'idle' && (
              <div style={{
                background: '#fb923c15',
                border: '2px solid #fb923c44',
                borderRadius: '12px',
                padding: '1.5rem',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🔄</div>
                <div style={{ color: '#fb923c', fontWeight: 'bold', marginBottom: '0.25rem' }}>
                  Strava Terhubung ✅
                </div>
                <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '1rem' }}>
                  Ambil aktivitas terbaru dari Strava untuk auto-fill data sesi.
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                  <button
                    onClick={handleStravaFetch}
                    disabled={stravaLoading}
                    style={{
                      padding: '0.65rem 1.5rem',
                      background: '#fb923c',
                      border: 'none',
                      borderRadius: '10px',
                      color: '#0f172a',
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                      cursor: stravaLoading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {stravaLoading ? 'Mengambil...' : '📥 Ambil Aktivitas'}
                  </button>
                  <button
                    onClick={() => { stravaDisconnect(profile.id); setStravaConnected(false); setStravaStep('idle') }}
                    style={{
                      padding: '0.65rem 1rem',
                      background: '#1e293b',
                      border: '1px solid #334155',
                      borderRadius: '10px',
                      color: '#94a3b8',
                      fontSize: '0.8rem',
                      cursor: 'pointer',
                    }}
                  >
                    Putuskan
                  </button>
                </div>
                {stravaError && (
                  <div style={{ color: '#f87171', fontSize: '0.75rem', marginTop: '0.75rem' }}>
                    {stravaError}
                  </div>
                )}
              </div>
            )}

            {/* Daftar aktivitas */}
            {stravaConnected && stravaStep === 'list' && (
              <>
                <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '0.75rem' }}>
                  Pilih aktivitas dari Strava:
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
                  {stravaActivities.length === 0 && (
                    <div style={{ color: '#475569', textAlign: 'center', padding: '2rem' }}>
                      Tidak ada aktivitas ditemukan.
                    </div>
                  )}
                  {stravaActivities.map(act => {
                    const speedKmh = act.average_speed
                      ? (act.average_speed * 3.6).toFixed(1)
                      : null
                    const distKm = act.distance
                      ? (act.distance / 1000).toFixed(1)
                      : null
                    return (
                      <div
                        key={act.id}
                        onClick={() => handleSelectActivity(act)}
                        style={{
                          background: '#1e293b',
                          border: '1px solid #334155',
                          borderRadius: '10px',
                          padding: '0.75rem 1rem',
                          cursor: 'pointer',
                          transition: 'border-color 0.15s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = '#fb923c'}
                        onMouseLeave={e => e.currentTarget.style.borderColor = '#334155'}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div>
                            <div style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>{act.name}</div>
                            <div style={{ color: '#94a3b8', fontSize: '0.7rem', marginTop: '2px' }}>
                              {formatStravaDate(act.start_date_local)} · {act.type}
                            </div>
                          </div>
                          <span style={{ color: '#fb923c', fontSize: '0.75rem', fontWeight: 'bold' }}>Pilih →</span>
                        </div>
                        <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                          {distKm && (
                            <span style={{ fontSize: '0.75rem', color: '#4ade80' }}>📍 {distKm} km</span>
                          )}
                          {speedKmh && (
                            <span style={{ fontSize: '0.75rem', color: '#facc15' }}>⚡ {speedKmh} km/j</span>
                          )}
                          {act.moving_time && (
                            <span style={{ fontSize: '0.75rem', color: '#a78bfa' }}>⏱ {formatDuration(act.moving_time)}</span>
                          )}
                          {act.average_heartrate && (
                            <span style={{ fontSize: '0.75rem', color: '#f472b6' }}>❤️ {Math.round(act.average_heartrate)} bpm</span>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
                <button
                  onClick={() => setStravaStep('idle')}
                  style={{
                    width: '100%', padding: '0.6rem',
                    background: 'none', border: '1px solid #334155',
                    borderRadius: '8px', color: '#94a3b8',
                    cursor: 'pointer', fontSize: '0.8rem',
                  }}
                >
                  ← Kembali
                </button>
              </>
            )}

            {/* Step RPE — setelah aktivitas dipilih */}
            {stravaConnected && stravaStep === 'rpe' && selectedActivity && (
              <>
                {/* Preview aktivitas yang dipilih */}
                <div style={{
                  background: '#fb923c18',
                  border: '2px solid #fb923c55',
                  borderRadius: '12px',
                  padding: '1rem',
                  marginBottom: '1rem',
                }}>
                  <div style={{ color: '#fb923c', fontWeight: 'bold', fontSize: '0.8rem', marginBottom: '0.5rem' }}>
                    ✅ Aktivitas dipilih
                  </div>
                  <div style={{ fontWeight: 'bold', fontSize: '0.95rem', marginBottom: '0.25rem' }}>
                    {selectedActivity.name}
                  </div>
                  <div style={{ color: '#94a3b8', fontSize: '0.7rem', marginBottom: '0.75rem' }}>
                    {formatStravaDate(selectedActivity.start_date_local)} · {selectedActivity.type}
                  </div>

                  {/* Data preview grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.4rem' }}>
                    {[
                      { label: 'Jarak', value: selectedActivity.mapped.distance ? `${selectedActivity.mapped.distance} km` : '—', color: '#4ade80' },
                      { label: 'Speed', value: selectedActivity.mapped.avg_speed ? `${selectedActivity.mapped.avg_speed} km/j` : '—', color: '#facc15' },
                      { label: 'Durasi', value: selectedActivity.moving_time ? formatDuration(selectedActivity.moving_time) : '—', color: '#a78bfa' },
                      { label: 'Cadence', value: selectedActivity.mapped.cadence ? `${selectedActivity.mapped.cadence} rpm` : '—', color: '#f472b6' },
                      { label: 'HR', value: selectedActivity.mapped.heart_rate ? `${selectedActivity.mapped.heart_rate} bpm` : '—', color: '#f87171' },
                      { label: 'Power', value: selectedActivity.mapped.power_watts ? `${selectedActivity.mapped.power_watts} W` : '—', color: '#fb923c' },
                    ].map(d => (
                      <div key={d.label} style={{
                        background: '#0f172a',
                        borderRadius: '6px',
                        padding: '0.4rem 0.5rem',
                        textAlign: 'center',
                      }}>
                        <div style={{ fontSize: '0.6rem', color: '#94a3b8' }}>{d.label}</div>
                        <div style={{ fontSize: '0.85rem', fontWeight: 'bold', color: d.color }}>{d.value}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* RPE selector */}
                <RPESelector />

                {/* Catatan */}
                <div style={{ marginBottom: '1.25rem' }}>
                  <label style={labelStyle}>Catatan tambahan (opsional)</label>
                  <textarea
                    placeholder="Bagaimana perasaan kamu hari ini?"
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    rows={2}
                    style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                  />
                </div>

                {error && (
                  <div style={{
                    background: '#ef444420', border: '1px solid #ef4444',
                    color: '#ef4444', padding: '0.75rem',
                    borderRadius: '8px', marginBottom: '1rem', fontSize: '0.85rem',
                  }}>
                    {error}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => { setStravaStep('list'); setRpe(''); setError(null) }}
                    style={{
                      flex: 1, padding: '0.75rem',
                      background: '#1e293b', border: '1px solid #334155',
                      borderRadius: '10px', color: '#94a3b8',
                      cursor: 'pointer', fontSize: '0.85rem',
                    }}
                  >
                    ← Ganti
                  </button>
                  <button
                    onClick={handleStravaSubmit}
                    disabled={loading}
                    style={{
                      flex: 2, padding: '0.75rem',
                      background: loading ? '#334155' : '#fb923c',
                      border: 'none',
                      borderRadius: '10px',
                      color: loading ? '#94a3b8' : '#0f172a',
                      fontWeight: 'bold', fontSize: '0.9rem',
                      cursor: loading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {loading ? 'Menyimpan...' : '💾 Simpan dari Strava'}
                  </button>
                </div>
              </>
            )}
          </>
        )}

        {/* ══════════════════════════════════════════════════
            MODE: FILE (.fit/.gpx)
        ══════════════════════════════════════════════════ */}
        {mode === 'file' && (
          <>
            {/* Step idle — drag & drop / pilih file */}
            {fileStep === 'idle' && (
              <div>
                <label
                  htmlFor="file-upload"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#38bdf815',
                    border: `2px dashed ${fileLoading ? '#38bdf8' : '#38bdf855'}`,
                    borderRadius: '12px',
                    padding: '2.5rem 1.5rem',
                    cursor: fileLoading ? 'not-allowed' : 'pointer',
                    transition: 'border-color 0.2s',
                  }}
                  onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#38bdf8' }}
                  onDragLeave={e => { e.currentTarget.style.borderColor = '#38bdf855' }}
                  onDrop={async e => {
                    e.preventDefault()
                    e.currentTarget.style.borderColor = '#38bdf855'
                    const file = e.dataTransfer.files?.[0]
                    if (file) handleFileUpload({ target: { files: [file] } })
                  }}
                >
                  <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>
                    {fileLoading ? '⏳' : '📁'}
                  </div>
                  <div style={{ color: '#38bdf8', fontWeight: 'bold', fontSize: '1rem', marginBottom: '0.25rem' }}>
                    {fileLoading ? 'Memproses file...' : 'Upload File Aktivitas'}
                  </div>
                  <div style={{ color: '#94a3b8', fontSize: '0.8rem', textAlign: 'center', lineHeight: 1.5 }}>
                    {fileLoading
                      ? `Parsing ${fileName}...`
                      : 'Klik atau drag & drop file .fit / .gpx ke sini'}
                  </div>
                  {!fileLoading && (
                    <div style={{
                      marginTop: '1rem',
                      display: 'flex',
                      gap: '0.5rem',
                    }}>
                      {['.fit', '.gpx'].map(ext => (
                        <span key={ext} style={{
                          padding: '2px 10px',
                          background: '#38bdf822',
                          border: '1px solid #38bdf844',
                          borderRadius: '999px',
                          color: '#38bdf8',
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                        }}>
                          {ext}
                        </span>
                      ))}
                    </div>
                  )}
                </label>
                <input
                  id="file-upload"
                  type="file"
                  accept=".fit,.gpx"
                  onChange={handleFileUpload}
                  disabled={fileLoading}
                  style={{ display: 'none' }}
                />

                {fileError && (
                  <div style={{
                    marginTop: '0.75rem',
                    background: '#ef444420',
                    border: '1px solid #ef444455',
                    borderRadius: '8px',
                    padding: '0.75rem',
                    color: '#f87171',
                    fontSize: '0.8rem',
                    lineHeight: 1.5,
                  }}>
                    ⚠️ {fileError}
                    {fileError.includes('fit-file-parser') && (
                      <div style={{ marginTop: '0.5rem', color: '#94a3b8', fontSize: '0.72rem' }}>
                        Tambahkan baris ini di <code style={{ color: '#38bdf8' }}>public/index.html</code> sebelum tag &lt;/body&gt;:<br />
                        <code style={{ color: '#a78bfa', wordBreak: 'break-all' }}>
                          {'<script src="https://cdn.jsdelivr.net/npm/fit-file-parser@1.9.8/dist/fit-file-parser.min.js"></script>'}
                        </code>
                      </div>
                    )}
                  </div>
                )}

                {/* Info .fit library */}
                <div style={{
                  marginTop: '0.75rem',
                  padding: '0.6rem 0.75rem',
                  background: '#1e293b',
                  borderRadius: '8px',
                  fontSize: '0.7rem',
                  color: '#475569',
                  lineHeight: 1.6,
                }}>
                  💡 File .gpx langsung bisa dipakai. Untuk .fit, pastikan sudah tambahkan script library di{' '}
                  <span style={{ color: '#38bdf8' }}>public/index.html</span>.
                </div>
              </div>
            )}

            {/* Step preview — tampilkan data hasil parse */}
            {fileStep === 'preview' && fileData && (
              <>
                <div style={{
                  background: '#38bdf818',
                  border: '2px solid #38bdf855',
                  borderRadius: '12px',
                  padding: '1rem',
                  marginBottom: '1rem',
                }}>
                  <div style={{ color: '#38bdf8', fontWeight: 'bold', fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                    ✅ File berhasil dibaca
                  </div>
                  <div style={{ color: '#94a3b8', fontSize: '0.7rem', marginBottom: '0.75rem' }}>
                    {fileName} · {fileData.source === 'file_gpx' ? 'GPX' : 'FIT'}
                    {fileData.notes && ` · ${fileData.notes}`}
                  </div>

                  {/* Preview data grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.4rem' }}>
                    {[
                      { label: 'Jarak',   value: fileData.distance    ? `${fileData.distance} km`    : '—', color: '#4ade80' },
                      { label: 'Speed',   value: fileData.avg_speed   ? `${fileData.avg_speed} km/j` : '—', color: '#facc15' },
                      { label: 'Durasi',  value: fileData.duration    ? `${fileData.duration} mnt`   : '—', color: '#a78bfa' },
                      { label: 'Cadence', value: fileData.cadence     ? `${fileData.cadence} rpm`    : '—', color: '#f472b6' },
                      { label: 'HR',      value: fileData.heart_rate  ? `${fileData.heart_rate} bpm` : '—', color: '#f87171' },
                      { label: 'Power',   value: fileData.power_watts ? `${fileData.power_watts} W`  : '—', color: '#fb923c' },
                    ].map(d => (
                      <div key={d.label} style={{
                        background: '#0f172a',
                        borderRadius: '6px',
                        padding: '0.4rem 0.5rem',
                        textAlign: 'center',
                      }}>
                        <div style={{ fontSize: '0.6rem', color: '#94a3b8' }}>{d.label}</div>
                        <div style={{ fontSize: '0.85rem', fontWeight: 'bold', color: d.value === '—' ? '#334155' : d.color }}>
                          {d.value}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* RPE selector */}
                <RPESelector />

                {/* Catatan */}
                <div style={{ marginBottom: '1.25rem' }}>
                  <label style={labelStyle}>Catatan tambahan (opsional)</label>
                  <textarea
                    placeholder="Bagaimana perasaan kamu hari ini?"
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    rows={2}
                    style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                  />
                </div>

                {error && (
                  <div style={{
                    background: '#ef444420', border: '1px solid #ef4444',
                    color: '#ef4444', padding: '0.75rem',
                    borderRadius: '8px', marginBottom: '1rem', fontSize: '0.85rem',
                  }}>
                    {error}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => { setFileStep('idle'); setFileData(null); setFileName(''); setRpe(''); setError(null) }}
                    style={{
                      flex: 1, padding: '0.75rem',
                      background: '#1e293b', border: '1px solid #334155',
                      borderRadius: '10px', color: '#94a3b8',
                      cursor: 'pointer', fontSize: '0.85rem',
                    }}
                  >
                    ← Ganti File
                  </button>
                  <button
                    onClick={handleFileSubmit}
                    disabled={loading}
                    style={{
                      flex: 2, padding: '0.75rem',
                      background: loading ? '#334155' : '#38bdf8',
                      border: 'none',
                      borderRadius: '10px',
                      color: loading ? '#94a3b8' : '#0f172a',
                      fontWeight: 'bold', fontSize: '0.9rem',
                      cursor: loading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {loading ? 'Menyimpan...' : '💾 Simpan dari File'}
                  </button>
                </div>
              </>
            )}
          </>
        )}

        {/* ══════════════════════════════════════════════════
            MODE: MANUAL
        ══════════════════════════════════════════════════ */}
        {mode === 'manual' && (
          <>
            {/* Road passing mode */}
            {isRoadPassing && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={labelStyle}>Mode Sesi</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {ROAD_MODES.map(m => (
                    <button key={m} onClick={() => setRoadMode(m)} style={{
                      flex: 1, padding: '0.5rem',
                      background: roadMode === m ? '#facc1522' : '#1e293b',
                      border: roadMode === m ? '2px solid #facc15' : '2px solid #334155',
                      borderRadius: '8px',
                      color: roadMode === m ? '#facc15' : '#94a3b8',
                      cursor: 'pointer', fontSize: '0.75rem', fontWeight: 'bold',
                    }}>
                      {m}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Long ride type */}
            {isLongRide && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={labelStyle}>Tipe Long Ride</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {LONG_RIDE_TYPES.map(t => (
                    <button key={t} onClick={() => setLongRideType(t)} style={{
                      flex: 1, padding: '0.5rem',
                      background: longRideType === t ? '#60a5fa22' : '#1e293b',
                      border: longRideType === t ? '2px solid #60a5fa' : '2px solid #334155',
                      borderRadius: '8px',
                      color: longRideType === t ? '#60a5fa' : '#94a3b8',
                      cursor: 'pointer', fontSize: '0.75rem', fontWeight: 'bold',
                    }}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* RPE */}
            <RPESelector />

            {/* Metrics grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <div>
                <label style={labelStyle}>Cadence (rpm)</label>
                <input type="number" placeholder="88" value={cadence}
                  onChange={e => setCadence(e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Avg Speed (km/jam)</label>
                <input type="number" placeholder="34.5" value={avgSpeed}
                  onChange={e => setAvgSpeed(e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Jarak (km)</label>
                <input type="number" placeholder="42.5" value={distance}
                  onChange={e => setDistance(e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Durasi (menit)</label>
                <input type="number" placeholder="90" value={duration}
                  onChange={e => setDuration(e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Heart Rate (bpm)</label>
                <input type="number" placeholder="155" value={heartRate}
                  onChange={e => setHeartRate(e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>
                  Power (watt) <span style={{ color: '#475569' }}>opsional</span>
                </label>
                <input type="number" placeholder="210" value={powerWatts}
                  onChange={e => setPowerWatts(e.target.value)} style={inputStyle} />
              </div>
            </div>

            {/* Hill repeat */}
            {isHillRepeat && (
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ color: '#f87171', fontWeight: 'bold', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                  🏔️ Detail Hill Repeat
                </div>
                {hillReps.map((s, si) => (
                  <div key={si} style={{ background: '#1e293b', borderRadius: '10px', padding: '0.75rem', marginBottom: '0.5rem' }}>
                    <div style={{ color: '#f87171', fontSize: '0.75rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                      Sesi {si + 1}
                    </div>
                    {s.reps.map((rep, ri) => (
                      <div key={ri} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '0.5rem', marginBottom: '0.4rem', alignItems: 'center' }}>
                        <div>
                          <label style={{ ...labelStyle, fontSize: '0.65rem' }}>Rep {ri + 1} — Durasi (detik)</label>
                          <input type="number" placeholder="120" value={rep.duration}
                            onChange={e => updateRep(si, ri, 'duration', e.target.value)}
                            style={{ ...inputStyle, padding: '0.4rem 0.5rem', fontSize: '0.85rem' }} />
                        </div>
                        <div>
                          <label style={{ ...labelStyle, fontSize: '0.65rem' }}>Speed (km/jam)</label>
                          <input type="number" placeholder="18.5" value={rep.speed}
                            onChange={e => updateRep(si, ri, 'speed', e.target.value)}
                            style={{ ...inputStyle, padding: '0.4rem 0.5rem', fontSize: '0.85rem' }} />
                        </div>
                        <button onClick={() => removeRep(si, ri)}
                          style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '1rem', marginTop: '1rem' }}>
                          ✕
                        </button>
                      </div>
                    ))}
                    <button onClick={() => addRep(si)} style={{
                      width: '100%', padding: '0.4rem',
                      background: 'none', border: '1px dashed #f8717155',
                      borderRadius: '6px', color: '#f87171',
                      cursor: 'pointer', fontSize: '0.75rem',
                    }}>
                      + Tambah Rep
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Catatan */}
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={labelStyle}>Catatan (opsional)</label>
              <textarea
                placeholder="Bagaimana perasaan kamu hari ini?"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={3}
                style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
              />
            </div>

            {error && (
              <div style={{
                background: '#ef444420', border: '1px solid #ef4444',
                color: '#ef4444', padding: '0.75rem',
                borderRadius: '8px', marginBottom: '1rem', fontSize: '0.85rem',
              }}>
                {error}
              </div>
            )}

            <button
              onClick={handleManualSubmit}
              disabled={loading}
              style={{
                width: '100%', padding: '0.85rem',
                background: loading ? '#334155' : today.color,
                color: loading ? '#94a3b8' : '#0f172a',
                border: 'none', borderRadius: '10px',
                fontSize: '1rem', fontWeight: 'bold',
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? 'Menyimpan...' : '💾 Simpan Sesi'}
            </button>
          </>
        )}
      </div>
    </div>
  )
}